import React, { useState, useEffect } from 'react';
import { 
  Compass, 
  BookOpen, 
  Layers, 
  Lightbulb, 
  Clock, 
  Edit3, 
  Save, 
  Upload, 
  X, 
  Eye, 
  Plus, 
  Trash2,
  Image as ImageIcon,
  CheckCircle2
} from 'lucide-react';
import { useToast } from '../../../components/Toast';
import { EDUCATION_DEFAULTS } from '../../../data/educationDefaults';
import { getImageUrl as resolveMediaSrc } from '../../../utils/mediaUtils';
import news1 from '../../../assets/news/news1.jpg';
import news4 from '../../../assets/news/news4.jpg';
import news7 from '../../../assets/news/news7.jpg';
import './FormsUnified.css';

const FALLBACK_STRATEGY_IMAGES = [news1, news4, news7];

const MAX_IMAGE_SIZE = 5 * 1024 * 1024;
const ALLOWED_TYPES = ['image/jpeg', 'image/png', 'image/webp'];

const getAuthHeaders = () => {
  const token = localStorage.getItem('ph_auth_token') || localStorage.getItem('auth_token') || sessionStorage.getItem('token');
  return {
    'Content-Type': 'application/json',
    ...(token ? { 'Authorization': `Bearer ${token}` } : {})
  };
};

export default function EducationLearningApproachForm({ apiBase }) {
  const { toast, ToastContainer } = useToast();
  const defaults = EDUCATION_DEFAULTS.learning_approach || {};

  const [formData, setFormData] = useState({
    kicker: defaults.kicker || 'Learning Approach',
    title: defaults.title || '5E Model, Inquiry, Project & Problem-Based Learning',
    subtitle: defaults.subtitle || 'A comprehensive framework that develops curiosity, collaboration, and mastery across every level.',
    description: defaults.description || '',
    pedagogicalModel: defaults.pedagogicalModel ? { ...defaults.pedagogicalModel } : {
      title: '5E Instructional Model',
      subtitle: 'Inquiry-based, project-based, or blended learning Pedagogical Model',
      description: 'Sekolah Permata Harapan Secondary adopts the 5E Instructional Model as a school-wide approach:',
      phases: []
    },
    signatureStrategies: defaults.signatureStrategies ? [...defaults.signatureStrategies] : [],
    integration: defaults.integration || ''
  });

  const [originalData, setOriginalData] = useState({ ...formData });
  const [pendingFields, setPendingFields] = useState({});
  const [loading, setLoading] = useState(true);
  const [sectionId, setSectionId] = useState(null);
  const [submitting, setSubmitting] = useState({});
  const [activeTab, setActiveTab] = useState('overview');
  const [imageUploading, setImageUploading] = useState(false);
  const [imageProgress, setImageProgress] = useState(0);
  const [editingPhaseIndex, setEditingPhaseIndex] = useState(null);
  const [phaseModalData, setPhaseModalData] = useState(null);
  const [editingStrategyIndex, setEditingStrategyIndex] = useState(null);
  const [strategyModalData, setStrategyModalData] = useState(null);
  const [mediaMap, setMediaMap] = useState({});
  const [lightboxImage, setLightboxImage] = useState(null);
  const [newOutcome, setNewOutcome] = useState('');

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiBase]);

  const loadMediaMap = async () => {
    try {
      const response = await fetch(
        `${apiBase}/education/cms/media?_t=${Date.now()}`,
        { 
          credentials: 'include',
          headers: { 
            'Cache-Control': 'no-cache', 
            'Pragma': 'no-cache',
            ...getAuthHeaders()
          }
        }
      );
      
      if (!response.ok) return;
      
      const result = await response.json();
      if (result.ok && Array.isArray(result.data)) {
        const map = {};
        result.data.forEach((media) => {
          const mediaId = media.id || media.media_id;
          const mediaUrl = media.url || media.file_path;
          if (mediaId && mediaUrl) {
            map[mediaId] = mediaUrl;
            map[String(mediaId)] = mediaUrl;
          }
        });
        setMediaMap(map);
      }
    } catch (err) {
      console.error('[EducationLearningApproachForm] Media fetch error:', err.message);
    }
  };

  const getMediaUrl = (mediaId) => {
    if (!mediaId) return null;
    if (mediaMap[mediaId]) return mediaMap[mediaId];
    if (mediaMap[String(mediaId)]) return mediaMap[String(mediaId)];
    return resolveMediaSrc(mediaId, mediaMap) || mediaId;
  };

  const loadData = async () => {
    await Promise.all([loadMediaMap(), loadSection()]);
  };

  const loadSection = async () => {
    setLoading(true);
    try {
      let section = null;
      
      const adminResponse = await fetch(
        `${apiBase}/education/cms/sections`,
        { credentials: 'include', headers: getAuthHeaders() }
      );
      
      if (adminResponse.ok) {
        const result = await adminResponse.json();
        if (result.ok && Array.isArray(result.data)) {
          section = result.data.find(s => s.section_type === 'education-learning-approach-shell') ||
                    result.data.find(s => s.section_type === 'education-learning-approach');
        }
      }
      
      if (!section) {
        const publicResponse = await fetch(
          `${apiBase}/education/cms/sections?published=true`,
          { credentials: 'include', headers: getAuthHeaders() }
        );
        
        if (publicResponse.ok) {
          const result = await publicResponse.json();
          if (result.ok && Array.isArray(result.data)) {
            section = result.data.find(s => s.section_type === 'education-learning-approach-shell') ||
                      result.data.find(s => s.section_type === 'education-learning-approach');
          }
        }
      }

      if (section) {
        setSectionId(section.id);

        let content = section.content_published || section.content || {};
        if (typeof content === 'string') {
          try { content = JSON.parse(content); } catch (e) { content = {}; }
        }
        const en = content.en || {};

        const initialData = {
          kicker: en.kicker || defaults.kicker || 'Learning Approach',
          title: en.title || defaults.title || '5E Model, Inquiry, Project & Problem-Based Learning',
          subtitle: en.subtitle || defaults.subtitle || 'A comprehensive framework that develops curiosity, collaboration, and mastery across every level.',
          description: en.description || defaults.description || '',
          pedagogicalModel: en.pedagogical_model || en.pedagogicalModel || defaults.pedagogicalModel || {
            title: '5E Instructional Model',
            subtitle: 'Inquiry-based, project-based, or blended learning Pedagogical Model',
            description: 'Sekolah Permata Harapan Secondary adopts the 5E Instructional Model as a school-wide approach:',
            phases: []
          },
          signatureStrategies: (en.signature_strategies && en.signature_strategies.length > 0)
            ? en.signature_strategies
            : (en.signatureStrategies && en.signatureStrategies.length > 0)
            ? en.signatureStrategies
            : (defaults.signatureStrategies || []),
          integration: en.integration || defaults.integration || ''
        };

        // Ensure deep phases array is cloned
        if (initialData.pedagogicalModel.phases) {
          initialData.pedagogicalModel.phases = initialData.pedagogicalModel.phases.map(p => ({ ...p }));
        } else {
          initialData.pedagogicalModel.phases = [];
        }

        // Ensure deep signatureStrategies array is cloned
        if (initialData.signatureStrategies) {
          initialData.signatureStrategies = initialData.signatureStrategies.map(s => ({
            ...s,
            outcomes: Array.isArray(s.outcomes) ? [...s.outcomes] : []
          }));
        } else {
          initialData.signatureStrategies = [];
        }

        const pendingFieldsMap = {};
        try {
          const pendingResponse = await fetch(
            `${apiBase}/cms/section/${section.id}/with-pending`,
            { credentials: 'include', headers: getAuthHeaders() }
          );
          
          if (pendingResponse.ok) {
            const pendingResult = await pendingResponse.json();
            if (pendingResult.success && pendingResult.data && pendingResult.data.pending_changes) {
              const { pending_changes } = pendingResult.data;
              if (Array.isArray(pending_changes)) {
                pending_changes.forEach(change => {
                  if (change.status === 'pending' && (change.locale === 'en' || !change.locale)) {
                    let val = change.new_value;
                    if (typeof val === 'string') {
                      try { val = JSON.parse(val); } catch (e) {}
                    }

                    if (change.field_id === 'kicker') {
                      pendingFieldsMap.kicker = true;
                      if (typeof val === 'string') initialData.kicker = val;
                    } else if (change.field_id === 'title') {
                      pendingFieldsMap.title = true;
                      if (typeof val === 'string') initialData.title = val;
                    } else if (change.field_id === 'subtitle') {
                      pendingFieldsMap.subtitle = true;
                      if (typeof val === 'string') initialData.subtitle = val;
                    } else if (change.field_id === 'description') {
                      pendingFieldsMap.description = true;
                      if (typeof val === 'string') initialData.description = val;
                    } else if (change.field_id === 'pedagogical_model.title') {
                      pendingFieldsMap['model.title'] = true;
                      if (typeof val === 'string') initialData.pedagogicalModel.title = val;
                    } else if (change.field_id === 'pedagogical_model.subtitle') {
                      pendingFieldsMap['model.subtitle'] = true;
                      if (typeof val === 'string') initialData.pedagogicalModel.subtitle = val;
                    } else if (change.field_id === 'pedagogical_model.description') {
                      pendingFieldsMap['model.description'] = true;
                      if (typeof val === 'string') initialData.pedagogicalModel.description = val;
                    } else if (change.field_id === 'pedagogical_model_header') {
                      pendingFieldsMap.model_header = true;
                      if (val && typeof val === 'object') {
                        initialData.pedagogicalModel.title = val.title || initialData.pedagogicalModel.title;
                        initialData.pedagogicalModel.subtitle = val.subtitle || initialData.pedagogicalModel.subtitle;
                        initialData.pedagogicalModel.description = val.description || initialData.pedagogicalModel.description;
                      }
                    } else if (change.field_id && change.field_id.includes('pedagogical_model.phases.')) {
                      const match = change.field_id.match(/pedagogical_model\.phases\.(\d+)\./);
                      if (match) {
                        const phaseIdx = parseInt(match[1], 10);
                        pendingFieldsMap[`phase_${phaseIdx}`] = true;
                        if (val && initialData.pedagogicalModel.phases[phaseIdx]) {
                          initialData.pedagogicalModel.phases[phaseIdx] = val;
                        }
                      }
                    } else if (change.field_id && change.field_id.includes('signature_strategies.')) {
                      const match = change.field_id.match(/signature_strategies\.(\d+)\./);
                      if (match) {
                        const strategyIdx = parseInt(match[1], 10);
                        pendingFieldsMap[`strategy_${strategyIdx}`] = true;
                        if (val && initialData.signatureStrategies[strategyIdx]) {
                          initialData.signatureStrategies[strategyIdx] = {
                            ...val,
                            outcomes: Array.isArray(val.outcomes) ? [...val.outcomes] : []
                          };
                        }
                      }
                    } else if (change.field_id === 'integration') {
                      pendingFieldsMap.integration = true;
                      if (typeof val === 'string') {
                        initialData.integration = val;
                      }
                    }
                  }
                });
              }
            }
          }
        } catch (err) {
          console.warn('[EducationLearningApproachForm] Pending changes check failed:', err.message);
        }

        setFormData(initialData);
        setOriginalData(JSON.parse(JSON.stringify(initialData)));
        setPendingFields(pendingFieldsMap);
      } else {
        toast.error('Learning Approach section not found');
      }
    } catch (err) {
      console.error('[EducationLearningApproachForm] Load error:', err);
      toast.error('Failed to load section: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleFieldChange = (key, value) => {
    setFormData(prev => ({
      ...prev,
      [key]: value
    }));
  };

  const handleModelHeaderChange = (key, value) => {
    setFormData(prev => ({
      ...prev,
      pedagogicalModel: {
        ...prev.pedagogicalModel,
        [key]: value
      }
    }));
  };

  // Submit Single Overview Field
  const handleSubmitSingleField = async (fieldName, label) => {
    if (!sectionId) {
      toast.error('Learning Approach section ID not loaded');
      return;
    }

    if (JSON.stringify(formData[fieldName]) === JSON.stringify(originalData[fieldName])) {
      toast.warning('No changes to submit');
      return;
    }

    setSubmitting(prev => ({ ...prev, [fieldName]: true }));

    try {
      const response = await fetch(`${apiBase}/cms/field-change/submit`, {
        method: 'POST',
        headers: getAuthHeaders(),
        credentials: 'include',
        body: JSON.stringify({
          section_id: sectionId,
          field_id: fieldName,
          field_name: label,
          old_value: originalData[fieldName] || '',
          new_value: formData[fieldName],
          locale: 'en'
        })
      });

      const result = await response.json();

      if (result.success || response.ok) {
        toast.success(`Proposed change for "${label}" submitted for Principal approval`);
        setPendingFields(prev => ({ ...prev, [fieldName]: true }));
        setOriginalData(prev => ({ ...prev, [fieldName]: formData[fieldName] }));
      } else {
        if (response.status === 409) {
          toast.warning(`A pending proposal for "${label}" is already awaiting review`);
        } else {
          toast.error(result.error || result.message || 'Submission failed');
        }
      }
    } catch (err) {
      console.error('Submit error:', err);
      toast.error(`Error submitting ${label}: ` + err.message);
    } finally {
      setSubmitting(prev => ({ ...prev, [fieldName]: false }));
    }
  };

  // Submit Single 5E Model Field
  const handleSubmitModelField = async (modelKey, label) => {
    if (!sectionId) {
      toast.error('Learning Approach section ID not loaded');
      return;
    }

    const fieldKey = `model.${modelKey}`;
    const currentValue = formData.pedagogicalModel?.[modelKey] || '';
    const originalValue = originalData.pedagogicalModel?.[modelKey] || '';

    if (currentValue === originalValue) {
      toast.warning('No changes to submit');
      return;
    }

    setSubmitting(prev => ({ ...prev, [fieldKey]: true }));

    try {
      const response = await fetch(`${apiBase}/cms/field-change/submit`, {
        method: 'POST',
        headers: getAuthHeaders(),
        credentials: 'include',
        body: JSON.stringify({
          section_id: sectionId,
          field_id: `pedagogical_model.${modelKey}`,
          field_name: `5E Model - ${label}`,
          old_value: originalValue,
          new_value: currentValue,
          locale: 'en'
        })
      });

      const result = await response.json();

      if (result.success || response.ok) {
        toast.success(`Proposed change for "${label}" submitted for Principal approval`);
        setPendingFields(prev => ({ ...prev, [fieldKey]: true }));
        setOriginalData(prev => ({
          ...prev,
          pedagogicalModel: {
            ...prev.pedagogicalModel,
            [modelKey]: currentValue
          }
        }));
      } else {
        if (response.status === 409) {
          toast.warning(`A pending proposal for "${label}" is already awaiting review`);
        } else {
          toast.error(result.error || result.message || 'Submission failed');
        }
      }
    } catch (err) {
      console.error('Submit error:', err);
      toast.error(`Error submitting ${label}: ` + err.message);
    } finally {
      setSubmitting(prev => ({ ...prev, [fieldKey]: false }));
    }
  };

  // Submit Integration description
  const handleSubmitIntegration = async () => {
    if (!sectionId) {
      toast.error('Learning Approach section ID not loaded');
      return;
    }

    if (formData.integration === originalData.integration) {
      toast.warning('No changes to submit');
      return;
    }

    setSubmitting(prev => ({ ...prev, integration: true }));

    try {
      const response = await fetch(
        `${apiBase}/cms/field-change/submit`,
        {
          method: 'POST',
          headers: getAuthHeaders(),
          credentials: 'include',
          body: JSON.stringify({
            section_id: sectionId,
            field_id: 'integration',
            field_name: 'Subject Integration Note',
            old_value: originalData.integration || '',
            new_value: formData.integration,
            locale: 'en'
          })
        }
      );

      const result = await response.json();

      if (result.success || response.ok) {
        toast.success('Subject Integration note proposed for Principal approval');
        setPendingFields(prev => ({ ...prev, integration: true }));
        setOriginalData(prev => ({ ...prev, integration: formData.integration }));
      } else {
        if (response.status === 409) {
          toast.warning('A pending proposal for Integration is already awaiting review');
        } else {
          toast.error(result.error || result.message || 'Submission failed');
        }
      }
    } catch (err) {
      console.error('Submit error:', err);
      toast.error('Error submitting integration: ' + err.message);
    } finally {
      setSubmitting(prev => ({ ...prev, integration: false }));
    }
  };

  // Phase Modal Editor
  const openEditPhaseModal = (index) => {
    setEditingPhaseIndex(index);
    setPhaseModalData({ ...formData.pedagogicalModel.phases[index] });
  };

  const closePhaseModal = () => {
    setEditingPhaseIndex(null);
    setPhaseModalData(null);
  };

  const handleSavePhase = async () => {
    if (!phaseModalData || !phaseModalData.phase?.trim()) {
      toast.warning('Please enter the phase name');
      return;
    }

    if (!sectionId) {
      toast.error('Section ID not loaded');
      return;
    }

    const phaseKey = `phase_${editingPhaseIndex}`;
    setSubmitting(prev => ({ ...prev, [phaseKey]: true }));

    try {
      const response = await fetch(
        `${apiBase}/cms/field-change/submit`,
        {
          method: 'POST',
          headers: getAuthHeaders(),
          credentials: 'include',
          body: JSON.stringify({
            section_id: sectionId,
            field_id: `pedagogical_model.phases.${editingPhaseIndex}.update`,
            field_name: `Update 5E Phase ${editingPhaseIndex + 1}: ${phaseModalData.phase}`,
            old_value: originalData.pedagogicalModel.phases[editingPhaseIndex] || {},
            new_value: phaseModalData,
            locale: 'en'
          })
        }
      );

      const result = await response.json();

      if (result.success || response.ok) {
        toast.success(`Phase "${phaseModalData.phase}" proposed for Principal approval`);
        
        setPendingFields(prev => ({
          ...prev,
          [`phase_${editingPhaseIndex}`]: true
        }));

        const updatedPhases = [...formData.pedagogicalModel.phases];
        updatedPhases[editingPhaseIndex] = { ...phaseModalData };
        setFormData(prev => ({
          ...prev,
          pedagogicalModel: {
            ...prev.pedagogicalModel,
            phases: updatedPhases
          }
        }));

        closePhaseModal();
      } else {
        if (response.status === 409) {
          toast.warning(`Phase ${editingPhaseIndex + 1} already has a pending proposal`);
        } else {
          toast.error(result.error || result.message || 'Submission failed');
        }
      }
    } catch (err) {
      console.error('Submit error:', err);
      toast.error('Failed to submit phase: ' + err.message);
    } finally {
      setSubmitting(prev => ({ ...prev, [phaseKey]: false }));
    }
  };

  // Strategy Modal Editor
  const openEditStrategyModal = (index) => {
    setEditingStrategyIndex(index);
    const strat = formData.signatureStrategies[index];
    setStrategyModalData({
      ...strat,
      outcomes: Array.isArray(strat.outcomes) ? [...strat.outcomes] : []
    });
    setNewOutcome('');
  };

  const closeStrategyModal = () => {
    setEditingStrategyIndex(null);
    setStrategyModalData(null);
    setNewOutcome('');
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!ALLOWED_TYPES.includes(file.type)) {
      toast.error('Only JPG, PNG, and WebP images are supported');
      return;
    }

    if (file.size > MAX_IMAGE_SIZE) {
      toast.error('File size exceeds 5MB limit');
      return;
    }

    setImageUploading(true);
    setImageProgress(0);

    try {
      const uploadData = new FormData();
      uploadData.append('file', file);

      const xhr = new XMLHttpRequest();

      xhr.upload.addEventListener('progress', (event) => {
        if (event.lengthComputable) {
          const percent = Math.round((event.loaded / event.total) * 100);
          setImageProgress(percent);
        }
      });

      xhr.addEventListener('load', () => {
        if (xhr.status === 200 || xhr.status === 201) {
          try {
            const resp = JSON.parse(xhr.responseText);
            if (resp.success || resp.ok) {
              const mediaId = resp.media_id || resp.id;
              const mediaUrl = resp.url || resp.file_path;

              if (mediaId) {
                setStrategyModalData(prev => ({ ...prev, image: String(mediaId) }));
                if (mediaUrl) {
                  setMediaMap(prev => ({
                    ...prev,
                    [mediaId]: mediaUrl,
                    [String(mediaId)]: mediaUrl
                  }));
                }
                toast.success('Strategy image uploaded successfully');
              }
            } else {
              toast.error(resp.error || 'Failed to upload image');
            }
          } catch (pe) {
            toast.error('Invalid server response');
          }
        } else {
          toast.error('Upload failed with status ' + xhr.status);
        }
        setImageUploading(false);
      });

      xhr.addEventListener('error', () => {
        toast.error('Network error during image upload');
        setImageUploading(false);
      });

      xhr.open('POST', `${apiBase}/education/cms/media/upload?section_type=education-learning-approach-shell`);
      xhr.withCredentials = true;
      const token = localStorage.getItem('ph_auth_token') || localStorage.getItem('auth_token') || sessionStorage.getItem('token');
      if (token) {
        xhr.setRequestHeader('Authorization', `Bearer ${token}`);
      }
      xhr.send(uploadData);
    } catch (err) {
      console.error('Image upload error:', err);
      toast.error('Upload failed: ' + err.message);
      setImageUploading(false);
    }
  };

  const handleAddOutcome = () => {
    if (!newOutcome.trim()) return;
    setStrategyModalData(prev => ({
      ...prev,
      outcomes: [...(prev.outcomes || []), newOutcome.trim()]
    }));
    setNewOutcome('');
  };

  const handleRemoveOutcome = (idx) => {
    setStrategyModalData(prev => ({
      ...prev,
      outcomes: prev.outcomes.filter((_, i) => i !== idx)
    }));
  };

  const handleSaveStrategy = async () => {
    if (!strategyModalData || !strategyModalData.title?.trim()) {
      toast.warning('Please enter strategy title');
      return;
    }

    if (!sectionId) {
      toast.error('Section ID not loaded');
      return;
    }

    const strategyKey = `strategy_${editingStrategyIndex}`;
    setSubmitting(prev => ({ ...prev, [strategyKey]: true }));

    try {
      const response = await fetch(
        `${apiBase}/cms/field-change/submit`,
        {
          method: 'POST',
          headers: getAuthHeaders(),
          credentials: 'include',
          body: JSON.stringify({
            section_id: sectionId,
            field_id: `signature_strategies.${editingStrategyIndex}.update`,
            field_name: `Update Strategy ${editingStrategyIndex + 1}: ${strategyModalData.title}`,
            old_value: originalData.signatureStrategies[editingStrategyIndex] || {},
            new_value: strategyModalData,
            locale: 'en'
          })
        }
      );

      const result = await response.json();

      if (result.success || response.ok) {
        toast.success(`Strategy "${strategyModalData.title}" proposed for Principal approval`);
        
        setPendingFields(prev => ({
          ...prev,
          [`strategy_${editingStrategyIndex}`]: true
        }));

        const updated = [...formData.signatureStrategies];
        updated[editingStrategyIndex] = { ...strategyModalData };
        setFormData(prev => ({
          ...prev,
          signatureStrategies: updated
        }));

        closeStrategyModal();
      } else {
        if (response.status === 409) {
          toast.warning(`Strategy ${editingStrategyIndex + 1} already has a pending proposal`);
        } else {
          toast.error(result.error || result.message || 'Submission failed');
        }
      }
    } catch (err) {
      console.error('Submit error:', err);
      toast.error('Failed to submit strategy: ' + err.message);
    } finally {
      setSubmitting(prev => ({ ...prev, [strategyKey]: false }));
    }
  };

  if (loading) {
    return (
      <div className="unified-form-container">
        <div className="unified-loading-container">
          <div className="unified-loading-spinner"></div>
          <p className="unified-loading-text">Loading Learning Approach & 5E Model content...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="unified-form-container">
      <ToastContainer />

      {/* Header */}
      <div className="unified-header">
        <div className="unified-header-left">
          <div className="unified-icon-badge">
            <Compass size={22} />
          </div>
          <div>
            <h2 className="unified-title">Learning Approach & 5E Model</h2>
            <p className="unified-subtitle">
              Manage pedagogical framework, 5E instructional phases, signature learning strategies, and subject integration
            </p>
          </div>
        </div>
      </div>

      {/* Sub-tabs Navigation */}
      <div className="unified-tab-bar" style={{ marginBottom: '24px' }}>
        <button
          type="button"
          className={`tab-pill-btn ${activeTab === 'overview' ? 'active' : ''}`}
          onClick={() => setActiveTab('overview')}
        >
          <BookOpen size={16} />
          Overview
        </button>
        <button
          type="button"
          className={`tab-pill-btn ${activeTab === 'phases' ? 'active' : ''}`}
          onClick={() => setActiveTab('phases')}
        >
          <Layers size={16} />
          5E Model & Phases ({formData.pedagogicalModel?.phases?.length || 0})
        </button>
        <button
          type="button"
          className={`tab-pill-btn ${activeTab === 'strategies' ? 'active' : ''}`}
          onClick={() => setActiveTab('strategies')}
        >
          <Lightbulb size={16} />
          Signature Strategies ({formData.signatureStrategies?.length || 0}) & Integration
        </button>
      </div>

      {/* TAB 1: OVERVIEW */}
      {activeTab === 'overview' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
          <div>
            <h3 style={{ fontSize: '16px', fontWeight: '800', color: '#0f172a', margin: 0 }}>
              Learning Approach Header & Overview
            </h3>
            <p style={{ fontSize: '13px', color: '#64748b', margin: '2px 0 0' }}>
              Primary kicker badge, section title headline, tagline subtitle, and section summary
            </p>
          </div>

          {/* Section Kicker */}
          <div className="unified-field-card">
            <div className="unified-field-header">
              <label className="unified-label">Section Tag / Kicker</label>
              {pendingFields.kicker && (
                <span className="unified-pending-pill">
                  <Clock size={12} /> Pending Review
                </span>
              )}
            </div>
            <div className="unified-input-action-row">
              <input
                type="text"
                className="unified-input"
                value={formData.kicker}
                onChange={(e) => handleFieldChange('kicker', e.target.value)}
                disabled={pendingFields.kicker || submitting.kicker}
                placeholder="e.g. Learning Approach"
                maxLength={60}
              />
              <button
                type="button"
                className="unified-submit-button"
                onClick={() => handleSubmitSingleField('kicker', 'Section Kicker')}
                disabled={submitting.kicker || pendingFields.kicker || formData.kicker === originalData.kicker}
              >
                <Save size={14} /> {submitting.kicker ? 'Saving...' : 'Save'}
              </button>
            </div>
          </div>

          {/* Section Title */}
          <div className="unified-field-card">
            <div className="unified-field-header">
              <label className="unified-label">Section Title / Headline</label>
              {pendingFields.title && (
                <span className="unified-pending-pill">
                  <Clock size={12} /> Pending Review
                </span>
              )}
            </div>
            <div className="unified-input-action-row">
              <input
                type="text"
                className="unified-input"
                value={formData.title}
                onChange={(e) => handleFieldChange('title', e.target.value)}
                disabled={pendingFields.title || submitting.title}
                placeholder="e.g. 5E Model, Inquiry, Project & Problem-Based Learning"
                maxLength={140}
              />
              <button
                type="button"
                className="unified-submit-button"
                onClick={() => handleSubmitSingleField('title', 'Section Title')}
                disabled={submitting.title || pendingFields.title || formData.title === originalData.title}
              >
                <Save size={14} /> {submitting.title ? 'Saving...' : 'Save'}
              </button>
            </div>
          </div>

          {/* Subtitle */}
          <div className="unified-field-card">
            <div className="unified-field-header">
              <label className="unified-label">Subtitle / Tagline</label>
              {pendingFields.subtitle && (
                <span className="unified-pending-pill">
                  <Clock size={12} /> Pending Review
                </span>
              )}
            </div>
            <div className="unified-input-action-row">
              <input
                type="text"
                className="unified-input"
                value={formData.subtitle}
                onChange={(e) => handleFieldChange('subtitle', e.target.value)}
                disabled={pendingFields.subtitle || submitting.subtitle}
                placeholder="e.g. A comprehensive framework that develops curiosity, collaboration, and mastery across every level."
                maxLength={200}
              />
              <button
                type="button"
                className="unified-submit-button"
                onClick={() => handleSubmitSingleField('subtitle', 'Section Subtitle')}
                disabled={submitting.subtitle || pendingFields.subtitle || formData.subtitle === originalData.subtitle}
              >
                <Save size={14} /> {submitting.subtitle ? 'Saving...' : 'Save'}
              </button>
            </div>
          </div>

          {/* Description */}
          <div className="unified-field-card">
            <div className="unified-field-header">
              <label className="unified-label">Section Overview / Summary (Optional)</label>
              {pendingFields.description && (
                <span className="unified-pending-pill">
                  <Clock size={12} /> Pending Review
                </span>
              )}
            </div>
            <div className="unified-input-action-row" style={{ alignItems: 'flex-start' }}>
              <textarea
                className="unified-textarea"
                value={formData.description}
                onChange={(e) => handleFieldChange('description', e.target.value)}
                disabled={pendingFields.description || submitting.description}
                rows={4}
                placeholder="Brief summary of our pedagogical approach across all educational levels..."
                maxLength={600}
              />
              <button
                type="button"
                className="unified-submit-button"
                style={{ marginTop: '2px' }}
                onClick={() => handleSubmitSingleField('description', 'Section Description')}
                disabled={submitting.description || pendingFields.description || formData.description === originalData.description}
              >
                <Save size={14} /> {submitting.description ? 'Saving...' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* TAB 2: 5E MODEL & PHASES */}
      {activeTab === 'phases' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
          {/* Model Header Card */}
          <div className="unified-field-card" style={{ borderLeftColor: '#6366f1' }}>
            <div className="unified-field-header">
              <label className="unified-label" style={{ color: '#4338ca' }}>
                5E Model Header & Description
              </label>
              {(pendingFields['model.title'] || pendingFields['model.subtitle'] || pendingFields['model.description']) && (
                <span className="unified-pending-pill">
                  <Clock size={12} /> Pending Review
                </span>
              )}
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              <div>
                <span style={{ fontSize: '11px', fontWeight: 700, color: '#64748b', textTransform: 'uppercase', display: 'block', marginBottom: '6px' }}>
                  Model Title
                </span>
                <div className="unified-input-action-row">
                  <input
                    type="text"
                    className="unified-input"
                    value={formData.pedagogicalModel?.title || ''}
                    onChange={(e) => handleModelHeaderChange('title', e.target.value)}
                    disabled={pendingFields['model.title'] || submitting['model.title']}
                    placeholder="e.g. 5E Instructional Model"
                  />
                  <button
                    type="button"
                    className="unified-submit-button"
                    onClick={() => handleSubmitModelField('title', 'Model Title')}
                    disabled={submitting['model.title'] || pendingFields['model.title'] || formData.pedagogicalModel?.title === originalData.pedagogicalModel?.title}
                  >
                    <Save size={14} /> {submitting['model.title'] ? 'Saving...' : 'Save'}
                  </button>
                </div>
              </div>

              <div>
                <span style={{ fontSize: '11px', fontWeight: 700, color: '#64748b', textTransform: 'uppercase', display: 'block', marginBottom: '6px' }}>
                  Model Subtitle
                </span>
                <div className="unified-input-action-row">
                  <input
                    type="text"
                    className="unified-input"
                    value={formData.pedagogicalModel?.subtitle || ''}
                    onChange={(e) => handleModelHeaderChange('subtitle', e.target.value)}
                    disabled={pendingFields['model.subtitle'] || submitting['model.subtitle']}
                    placeholder="e.g. Inquiry-based, project-based, or blended learning Pedagogical Model"
                  />
                  <button
                    type="button"
                    className="unified-submit-button"
                    onClick={() => handleSubmitModelField('subtitle', 'Model Subtitle')}
                    disabled={submitting['model.subtitle'] || pendingFields['model.subtitle'] || formData.pedagogicalModel?.subtitle === originalData.pedagogicalModel?.subtitle}
                  >
                    <Save size={14} /> {submitting['model.subtitle'] ? 'Saving...' : 'Save'}
                  </button>
                </div>
              </div>

              <div>
                <span style={{ fontSize: '11px', fontWeight: 700, color: '#64748b', textTransform: 'uppercase', display: 'block', marginBottom: '6px' }}>
                  Model Overview / Philosophy
                </span>
                <div className="unified-input-action-row" style={{ alignItems: 'flex-start' }}>
                  <textarea
                    className="unified-textarea"
                    value={formData.pedagogicalModel?.description || ''}
                    onChange={(e) => handleModelHeaderChange('description', e.target.value)}
                    disabled={pendingFields['model.description'] || submitting['model.description']}
                    rows={3}
                    placeholder="Sekolah Permata Harapan Secondary adopts the 5E Instructional Model as a school-wide approach:"
                  />
                  <button
                    type="button"
                    className="unified-submit-button"
                    style={{ marginTop: '2px' }}
                    onClick={() => handleSubmitModelField('description', 'Model Description')}
                    disabled={submitting['model.description'] || pendingFields['model.description'] || formData.pedagogicalModel?.description === originalData.pedagogicalModel?.description}
                  >
                    <Save size={14} /> {submitting['model.description'] ? 'Saving...' : 'Save'}
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Phases List */}
          <div>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '14px' }}>
              <div>
                <h3 style={{ fontSize: '15px', fontWeight: 800, color: '#0f172a', margin: 0 }}>
                  5E Instructional Phases ({formData.pedagogicalModel?.phases?.length || 0})
                </h3>
                <p style={{ fontSize: '12.5px', color: '#64748b', margin: '2px 0 0' }}>
                  The 5 structured stages (Engage, Explore, Explain, Elaborate, Evaluate) with automatic preset educational icons
                </p>
              </div>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
              {Array.isArray(formData.pedagogicalModel?.phases) && formData.pedagogicalModel.phases.map((phase, idx) => {
                const isPending = pendingFields[`phase_${idx}`];
                return (
                  <div
                    key={idx}
                    className="unified-field-card"
                    style={{
                      borderLeftColor: idx % 2 === 0 ? '#2563eb' : '#8b5cf6',
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '12px'
                    }}
                  >
                    <div className="unified-field-header">
                      <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <span style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          width: '28px',
                          height: '28px',
                          borderRadius: '8px',
                          background: '#eff6ff',
                          color: '#2563eb',
                          fontWeight: 800,
                          fontSize: '13px'
                        }}>
                          {idx + 1}
                        </span>
                        <div>
                          <h4 style={{ fontSize: '15px', fontWeight: 800, color: '#0f172a', margin: 0 }}>
                            {phase.phase}
                          </h4>
                          <span style={{ fontSize: '12px', color: '#64748b' }}>
                            {phase.description}
                          </span>
                        </div>
                      </div>

                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        {isPending && (
                          <span className="unified-pending-pill">
                            <Clock size={12} /> Pending Review
                          </span>
                        )}
                        <button
                          type="button"
                          className="unified-secondary-btn"
                          onClick={() => openEditPhaseModal(idx)}
                          disabled={isPending}
                        >
                          <Edit3 size={14} />
                          Edit Phase
                        </button>
                      </div>
                    </div>

                    <div style={{
                      background: '#f8fafc',
                      padding: '12px 14px',
                      borderRadius: '8px',
                      border: '1px solid #f1f5f9',
                      fontSize: '13.5px',
                      color: '#334155',
                      lineHeight: 1.5
                    }}>
                      <span style={{ fontWeight: 600, color: '#0f172a', marginRight: '6px' }}>
                        Classroom Action:
                      </span>
                      {phase.details || 'No detailed instructions provided.'}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* TAB 3: STRATEGIES & INTEGRATION */}
      {activeTab === 'strategies' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
          {/* Strategies List */}
          <div>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '14px' }}>
              <div>
                <h3 style={{ fontSize: '15px', fontWeight: 800, color: '#0f172a', margin: 0 }}>
                  Signature Learning Strategies ({formData.signatureStrategies?.length || 0})
                </h3>
                <p style={{ fontSize: '12.5px', color: '#64748b', margin: '2px 0 0' }}>
                  Active learning methodologies (IBL, PBL, PrBL) with illustration images and key outcomes
                </p>
              </div>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              {Array.isArray(formData.signatureStrategies) && formData.signatureStrategies.map((strategy, idx) => {
                const isPending = pendingFields[`strategy_${idx}`];
                const imgSrc = getMediaUrl(strategy.image) || FALLBACK_STRATEGY_IMAGES[idx % FALLBACK_STRATEGY_IMAGES.length];

                return (
                  <div
                    key={idx}
                    className="unified-field-card"
                    style={{
                      borderLeftColor: '#0d9488',
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '14px'
                    }}
                  >
                    <div className="unified-field-header">
                      <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <span style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          width: '28px',
                          height: '28px',
                          borderRadius: '8px',
                          background: '#f0fdfa',
                          color: '#0d9488',
                          fontWeight: 800,
                          fontSize: '13px'
                        }}>
                          {idx + 1}
                        </span>
                        <h4 style={{ fontSize: '16px', fontWeight: 800, color: '#0f172a', margin: 0 }}>
                          {strategy.title}
                        </h4>
                      </div>

                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        {isPending && (
                          <span className="unified-pending-pill">
                            <Clock size={12} /> Pending Review
                          </span>
                        )}
                        <button
                          type="button"
                          className="unified-secondary-btn"
                          onClick={() => openEditStrategyModal(idx)}
                          disabled={isPending}
                        >
                          <Edit3 size={14} />
                          Edit Strategy
                        </button>
                      </div>
                    </div>

                    <div style={{ display: 'flex', gap: '16px', alignItems: 'flex-start' }}>
                      {/* Image Thumbnail with Lightbox */}
                      {imgSrc ? (
                        <div
                          style={{
                            width: '120px',
                            height: '80px',
                            borderRadius: '8px',
                            overflow: 'hidden',
                            position: 'relative',
                            cursor: 'pointer',
                            flexShrink: 0,
                            border: '1.5px solid #e2e8f0'
                          }}
                          onClick={() => setLightboxImage(imgSrc)}
                          title="Click to preview full image"
                        >
                          <img
                            src={imgSrc}
                            alt={strategy.title}
                            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                          />
                          <div style={{
                            position: 'absolute',
                            inset: 0,
                            background: 'rgba(0,0,0,0.25)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            opacity: 0,
                            transition: 'opacity 0.2s',
                            color: '#ffffff'
                          }}
                          onMouseEnter={(e) => e.currentTarget.style.opacity = '1'}
                          onMouseLeave={(e) => e.currentTarget.style.opacity = '0'}
                          >
                            <Eye size={18} />
                          </div>
                        </div>
                      ) : (
                        <div style={{
                          width: '120px',
                          height: '80px',
                          borderRadius: '8px',
                          background: '#f8fafc',
                          border: '1.5px dashed #cbd5e1',
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          justifyContent: 'center',
                          gap: '4px',
                          color: '#94a3b8',
                          fontSize: '11px',
                          flexShrink: 0
                        }}>
                          <ImageIcon size={20} />
                          <span>No Image</span>
                        </div>
                      )}

                      <div style={{ flex: 1, minWidth: 0 }}>
                        <p style={{ fontSize: '13.5px', color: '#475569', margin: '0 0 10px 0', lineHeight: 1.5 }}>
                          {strategy.description}
                        </p>

                        {Array.isArray(strategy.outcomes) && strategy.outcomes.length > 0 && (
                          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                            {strategy.outcomes.map((outcome, oIdx) => (
                              <span
                                key={oIdx}
                                style={{
                                  background: '#f1f5f9',
                                  color: '#334155',
                                  padding: '3px 8px',
                                  borderRadius: '6px',
                                  fontSize: '11.5px',
                                  fontWeight: 600,
                                  border: '1px solid #e2e8f0',
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                  gap: '4px'
                                }}
                              >
                                <CheckCircle2 size={11} color="#0d9488" />
                                {outcome}
                              </span>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Subject Integration Card */}
          <div className="unified-field-card" style={{ borderLeftColor: '#f59e0b', marginTop: '8px' }}>
            <div className="unified-field-header">
              <div>
                <label className="unified-label" style={{ color: '#b45309' }}>
                  Subject Integration Note
                </label>
                <p style={{ fontSize: '12.5px', color: '#64748b', margin: '2px 0 0' }}>
                  Holistic pedagogy note showing how strategies are applied across all subjects (Humanities, Science, etc.)
                </p>
              </div>
              {pendingFields.integration && (
                <span className="unified-pending-pill">
                  <Clock size={12} /> Pending Review
                </span>
              )}
            </div>
            <div className="unified-input-action-row" style={{ alignItems: 'flex-start' }}>
              <textarea
                className="unified-textarea"
                value={formData.integration}
                onChange={(e) => handleFieldChange('integration', e.target.value)}
                disabled={pendingFields.integration || submitting.integration}
                rows={3}
                placeholder="e.g. These strategies are integrated across all subjects, especially in Humanities, Science, and Global Perspectives..."
              />
              <button
                type="button"
                className="unified-submit-button"
                style={{ marginTop: '2px' }}
                onClick={handleSubmitIntegration}
                disabled={submitting.integration || pendingFields.integration || formData.integration === originalData.integration}
              >
                <Save size={14} /> {submitting.integration ? 'Saving...' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* PHASE EDIT MODAL */}
      {editingPhaseIndex !== null && phaseModalData && (
        <div className="unified-modal-overlay" onClick={closePhaseModal}>
          <div className="unified-modal" onClick={(e) => e.stopPropagation()}>
            <div className="unified-modal-header">
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <Layers size={20} color="#2563eb" />
                <h3 style={{ margin: 0, fontSize: '18px', fontWeight: 800, color: '#0f172a' }}>
                  Edit 5E Phase: {phaseModalData.phase}
                </h3>
              </div>
              <button
                type="button"
                onClick={closePhaseModal}
                style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#64748b' }}
              >
                <X size={20} />
              </button>
            </div>

            <div className="unified-modal-body" style={{ display: 'flex', flexDirection: 'column', gap: '18px' }}>
              {/* Icon Info Alert */}
              <div style={{
                padding: '10px 14px',
                background: '#f0fdf4',
                border: '1px solid #bbf7d0',
                borderRadius: '8px',
                fontSize: '12.5px',
                color: '#166534',
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}>
                <CheckCircle2 size={16} color="#16a34a" style={{ flexShrink: 0 }} />
                <span>Icon diatur otomatis oleh sistem berdasarkan tahapan pedagogis 5E agar tampilan di halaman utama selalu rapi dan konsisten.</span>
              </div>

              <div>
                <label className="unified-label" style={{ marginBottom: '8px', display: 'block' }}>
                  Phase Name *
                </label>
                <input
                  type="text"
                  className="unified-input"
                  value={phaseModalData.phase || ''}
                  onChange={(e) => setPhaseModalData(prev => ({ ...prev, phase: e.target.value }))}
                  placeholder="e.g. Engage, Explore, Explain, Elaborate, Evaluate"
                  maxLength={50}
                />
              </div>

              <div>
                <label className="unified-label" style={{ marginBottom: '8px', display: 'block' }}>
                  Core Purpose / Description *
                </label>
                <input
                  type="text"
                  className="unified-input"
                  value={phaseModalData.description || ''}
                  onChange={(e) => setPhaseModalData(prev => ({ ...prev, description: e.target.value }))}
                  placeholder="e.g. Activate curiosity and prior knowledge"
                  maxLength={120}
                />
              </div>

              <div>
                <label className="unified-label" style={{ marginBottom: '8px', display: 'block' }}>
                  Pedagogical Details / Classroom Application *
                </label>
                <textarea
                  className="unified-textarea"
                  value={phaseModalData.details || ''}
                  onChange={(e) => setPhaseModalData(prev => ({ ...prev, details: e.target.value }))}
                  rows={4}
                  placeholder="Provide comprehensive details on teacher guidance and student activity during this phase..."
                  maxLength={500}
                />
              </div>
            </div>

            <div className="unified-modal-footer">
              <button type="button" className="unified-secondary-btn" onClick={closePhaseModal}>
                Cancel
              </button>
              <button
                type="button"
                className="unified-submit-button"
                onClick={handleSavePhase}
                disabled={submitting[`phase_${editingPhaseIndex}`]}
              >
                <Save size={16} />
                {submitting[`phase_${editingPhaseIndex}`] ? 'Submitting...' : 'Submit Phase Proposal'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* STRATEGY EDIT MODAL */}
      {editingStrategyIndex !== null && strategyModalData && (
        <div className="unified-modal-overlay" onClick={closeStrategyModal}>
          <div className="unified-modal" onClick={(e) => e.stopPropagation()}>
            <div className="unified-modal-header">
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <Lightbulb size={20} color="#0d9488" />
                <h3 style={{ margin: 0, fontSize: '18px', fontWeight: 800, color: '#0f172a' }}>
                  Edit Strategy: {strategyModalData.title}
                </h3>
              </div>
              <button
                type="button"
                onClick={closeStrategyModal}
                style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#64748b' }}
              >
                <X size={20} />
              </button>
            </div>

            <div className="unified-modal-body" style={{ display: 'flex', flexDirection: 'column', gap: '18px' }}>
              {/* Media Upload & Preview - REPLACE ONLY, NO REMOVE */}
              <div>
                <label className="unified-label" style={{ marginBottom: '8px', display: 'block' }}>
                  Strategy Illustration / Image
                </label>

                {strategyModalData.image && getMediaUrl(strategyModalData.image) && (
                  <div style={{ marginBottom: '12px', display: 'flex', alignItems: 'center', gap: '14px' }}>
                    <div style={{
                      width: '140px',
                      height: '90px',
                      borderRadius: '8px',
                      overflow: 'hidden',
                      border: '1.5px solid #e2e8f0'
                    }}>
                      <img
                        src={getMediaUrl(strategyModalData.image)}
                        alt="Preview"
                        style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                      />
                    </div>
                  </div>
                )}

                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <label
                    htmlFor="strategy-image-file"
                    className="unified-secondary-btn"
                    style={{ cursor: imageUploading ? 'not-allowed' : 'pointer' }}
                  >
                    <Upload size={14} />
                    {imageUploading ? `Uploading (${imageProgress}%)...` : (strategyModalData.image ? 'Replace Image' : 'Upload Image')}
                  </label>
                  <input
                    id="strategy-image-file"
                    type="file"
                    accept="image/jpeg,image/png,image/webp"
                    onChange={handleImageUpload}
                    disabled={imageUploading}
                    style={{ display: 'none' }}
                  />
                  <span style={{ fontSize: '12px', color: '#64748b' }}>
                    Max 5MB • JPG, PNG, WebP (Ganti gambar langsung tanpa hapus)
                  </span>
                </div>
              </div>

              <div>
                <label className="unified-label" style={{ marginBottom: '8px', display: 'block' }}>
                  Strategy Title *
                </label>
                <input
                  type="text"
                  className="unified-input"
                  value={strategyModalData.title || ''}
                  onChange={(e) => setStrategyModalData(prev => ({ ...prev, title: e.target.value }))}
                  placeholder="e.g. Inquiry-Based Learning (IBL)"
                  maxLength={100}
                />
              </div>

              <div>
                <label className="unified-label" style={{ marginBottom: '8px', display: 'block' }}>
                  Description *
                </label>
                <textarea
                  className="unified-textarea"
                  value={strategyModalData.description || ''}
                  onChange={(e) => setStrategyModalData(prev => ({ ...prev, description: e.target.value }))}
                  rows={3}
                  placeholder="Describe the approach and its impact on student learning..."
                  maxLength={500}
                />
              </div>

              {/* Outcomes Manager */}
              <div>
                <label className="unified-label" style={{ marginBottom: '8px', display: 'block' }}>
                  Key Learning Outcomes
                </label>
                
                <div style={{ display: 'flex', gap: '8px', marginBottom: '10px' }}>
                  <input
                    type="text"
                    className="unified-input"
                    value={newOutcome}
                    onChange={(e) => setNewOutcome(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        handleAddOutcome();
                      }
                    }}
                    placeholder="e.g. Critical thinking and evidence-backed conclusions"
                  />
                  <button
                    type="button"
                    className="unified-secondary-btn"
                    onClick={handleAddOutcome}
                  >
                    <Plus size={14} />
                    Add
                  </button>
                </div>

                <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                  {strategyModalData.outcomes?.map((outcome, oIdx) => (
                    <div
                      key={oIdx}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        background: '#f8fafc',
                        border: '1px solid #e2e8f0',
                        borderRadius: '6px',
                        padding: '6px 12px'
                      }}
                    >
                      <span style={{ fontSize: '13px', color: '#1e293b' }}>{outcome}</span>
                      <button
                        type="button"
                        onClick={() => handleRemoveOutcome(oIdx)}
                        style={{ background: 'none', border: 'none', color: '#ef4444', cursor: 'pointer', padding: '2px' }}
                        title="Remove outcome"
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <div className="unified-modal-footer">
              <button type="button" className="unified-secondary-btn" onClick={closeStrategyModal}>
                Cancel
              </button>
              <button
                type="button"
                className="unified-submit-button"
                onClick={handleSaveStrategy}
                disabled={submitting[`strategy_${editingStrategyIndex}`]}
              >
                <Save size={16} />
                {submitting[`strategy_${editingStrategyIndex}`] ? 'Submitting...' : 'Submit Strategy Proposal'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* LIGHTBOX PREVIEW MODAL */}
      {lightboxImage && (
        <div className="unified-modal-overlay" onClick={() => setLightboxImage(null)}>
          <div
            style={{
              position: 'relative',
              maxWidth: '85vw',
              maxHeight: '85vh',
              borderRadius: '12px',
              overflow: 'hidden',
              background: '#000000',
              boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.5)'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <button
              type="button"
              onClick={() => setLightboxImage(null)}
              style={{
                position: 'absolute',
                top: '12px',
                right: '12px',
                background: 'rgba(0,0,0,0.6)',
                color: '#ffffff',
                border: 'none',
                borderRadius: '50%',
                width: '32px',
                height: '32px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                zIndex: 10
              }}
            >
              <X size={18} />
            </button>
            <img
              src={lightboxImage}
              alt="Full Preview"
              style={{
                width: '100%',
                height: '100%',
                maxHeight: '85vh',
                objectFit: 'contain',
                display: 'block'
              }}
            />
          </div>
        </div>
      )}
    </div>
  );
}
